/// <amd-module name="Test/TestResultDetails/Components/ResultDetailsHeader" />
import * as React from "react";
import { ViewContext } from "Test/common-util/Common";
import { defaultComparer, IconDetails } from "Test/common-util/Common.Utils";
import { TestOutcome } from "Test/common-util/Contracts/Test";
import * as Resources from "Test/common-util/Resources";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { getFriendlyName, getIconDetails } from "Test/common-util/TestOutcomeUtils";
import { TagsList } from "Test/TagsList/Components/TagsList";
import { TestMode } from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Accordion, IAccordionSection } from "VSSUI/Accordion";
import { Icon } from "VSSUI/Icon";
import { Tooltip } from "VSSUI/TooltipEx";
import { ago } from "VSSUI/Utilities/Date";
import { TestResultDetailsActionCreator } from "Test/TestResultDetails/Actions/TestResultDetailsActionCreator";

export interface IResultHeaderDetailsProps extends IVssComponentProperties {
    outcome?: TestOutcome;
    duration?: string;
    startedDate?: Date;
    completedDate?: Date;
    state?: string;
    failingContextName?: string;
    failingContextId?: number;
    owner?: string;
    computerName?: string;
    testMode: TestMode;
    viewContext?: ViewContext;
    isCurrentArtifact?: boolean;
    detailsPaneMode?: string;
    tags?: string[];
    failingSince?: string;
    currentHeadeState?: boolean;
    testResultDetailsActionCreator?: TestResultDetailsActionCreator;
}

export class ResultDetailsHeaderComponent extends VssComponent<IResultHeaderDetailsProps, {}, IVssComponentContext> {

    constructor(props: IResultHeaderDetailsProps, context: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        if(this.props.testMode === TestMode.TestRun) {
            return this._renderHeader();
        }
        else {

            const headerDeatils: IAccordionSection[] = [{
                isCollapsed: new ObservableValue(this.props.currentHeadeState!),
                key: "headerInfo",
                title: Resources.Header_Information,
                onRenderContent: () => { return this._renderHeader(); }
            }]

            return <Accordion sections={headerDeatils} className="test-result-header-accordion" onSectionClicked = {this._onSectionClicked}/>
        }
    }

    public _renderHeader(): JSX.Element {
        let infoToRenderInHeader: JSX.Element[] = [];

        if(this.props.testMode === TestMode.TestResult && this.props.state) {
            infoToRenderInHeader.push(this._getResultOutcomeField());
        }

        if(this.props.testMode === TestMode.TestRun && this.props.state){
            infoToRenderInHeader.push(this._getRunStateField());
        }

        infoToRenderInHeader.push(this._getDurationField());

        if(this.props.testMode === TestMode.TestResult) {
            infoToRenderInHeader.push(this._getResultOwnerField());

            if(this.props.startedDate) {
                infoToRenderInHeader.push(this._getDateStartedField());
            }

            if(this.props.completedDate) {
                infoToRenderInHeader.push(this._getDateCompletedField());
            }
            
            if(this.props.failingSince) {
                infoToRenderInHeader.push(this._getFailingSinceField());
            }
            
            if(this.props.failingContextName) {
                infoToRenderInHeader.push(this._getFailingBuildReleaseLinkDiv());
            }
        }

        if(this.props.tags && this.props.tags.length > 0) {
            infoToRenderInHeader.push(this._getTagsField());
        }

        return (
            <div className="flex-row test-result-header-content">
                {
                    infoToRenderInHeader
                }
            </div>);
    }

    private _onSectionClicked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, section: IAccordionSection): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
        this.props.testResultDetailsActionCreator!.saveHeaderState(collapsed.value);
    };

    private _getTagsField(): JSX.Element {
        return (
            <div className="result-header-tag-info flex-row" key="tag">
                <label className="tags-label">{Resources.TagsText}</label>
                {
                    <div className="tag-list-info">
                        <TagsList
                            tags={this.props.tags ? this.props.tags : []}>
                        </TagsList>
                    </div>
                }
            </div>
        );
    }

    private _getRunStateField(): JSX.Element {
        const completedSince: string = this._calculateCompletedSince();
        return (
            this.props.state
                ? <div className="result-header-info result-outcome-status" key="runState">
                    <span className="run-outcome-status">
                        {
                            format(
                                Resources.ResultOutcomeInfo,
                                this.props.state,
                                completedSince
                            )
                        }
                    </span>
                </div>
                : <div key="emptyRunState"></div>
        );
    }

    private _getResultOutcomeField(): JSX.Element {
        return (
            <div className="result-header-info result-outcome-status flex-row" key="outcome">
                <span>
                    {this._getOutcomeIcon()}
                </span>
                <span className="result-outcome-status-computerName">
                    {this._getOutcomeAndComputerNameInfoText()}
                </span>
            </div>
        );
    }

    private _getDurationField(): JSX.Element {
        return (
            <div className="result-header-info run-duration-info" key="duration">
                <label className="result-header-label">
                    {Resources.ResultGridHeader_Duration}
                </label>
                <Tooltip text={this.props.duration}>
                    <span className="run-duration">{this.props.duration}</span>
                </Tooltip>
            </div>
        );
    }

    private _getDateStartedField(): JSX.Element {
        return (
            <div className="result-header-info run-date-started-info" key="dateStarted">
                <label className="result-header-label">
                    {Resources.ResultHeader_DateStarted}
                </label>
                <Tooltip text={this.props.startedDate!.toLocaleString()}>
                    <span className="date-started">{this.props.startedDate!.toLocaleString()}</span>
                </Tooltip>
            </div>
        );
    }

    private _getDateCompletedField(): JSX.Element {
        return (
            <div className="result-header-info run-date-completed-info" key="dateCompleted">
                <label className="result-header-label">
                    {Resources.ResultHeader_DateCompleted}
                </label>
                <Tooltip text={this.props.completedDate!.toLocaleString()}>
                    <span className="date-completed">{this.props.completedDate!.toLocaleString()}</span>
                </Tooltip>
            </div>
        );
    }

    private _getFailingSinceField(): JSX.Element {
        return (
            <div className="result-header-info run-failing-since-info" key="failingSince">
                <label className="result-header-label">
                    {Resources.ResultGridHeader_FailingSince}
                </label>
                <Tooltip text={this.props.failingSince}>
                    <span className="failing-since">{this.props.failingSince}</span>
                </Tooltip>
            </div>
        );
    }

    private _getResultOwnerField(): JSX.Element {
        const owner: string = this.props.owner
            ? this.props.owner
            : Resources.NotAvailableText;

        return (
            <div className="result-header-info owner-info" key="owner">
                <label className="owner-label">{Resources.Owner}</label>
                {
                    <Tooltip text={owner}>
                        <span className="owner-name">{owner}</span>
                    </Tooltip>
                }
            </div>
        );
    }

    private _getFailingBuildReleaseLinkDiv(): JSX.Element {
        let failingContextLabel: string = "";
        let failingUrl: string = "";
        let telemetryEvent: string = "";

        if (this.props.viewContext === ViewContext.Build && this.props.failingContextId) {
            failingContextLabel = Resources.ResultGridHeader_FailingBuild;
            failingUrl = this._getBuildUrl(this.props.failingContextId);
            telemetryEvent = TelemetryConstants.featureTestTab_BuildLinkClicked;
        } else if (this.props.viewContext === ViewContext.Release && this.props.failingContextId) {
            failingContextLabel = Resources.ResultGridHeader_FailingRelease;
            failingUrl = this._getReleaseUrl(this.props.failingContextId);
            telemetryEvent = TelemetryConstants.featureTestTab_ReleaseLinkClicked;
        }

        return <div className="result-header-info artifact-info" key="artifactInfo">
            <label className="failingContext-label">{failingContextLabel}</label>
            <Tooltip text={this.props.failingContextName}>
                <span className="failingContext-name">
                    {
                        this.props.isCurrentArtifact ? this.props.failingContextName : this._getFailingArtifactLink(failingUrl, telemetryEvent)
                    }
                </span>
            </Tooltip>
        </div>;
    }

    private _getFailingArtifactLink(failingUrl: string, telemetryEvent: string): JSX.Element {
        return <FPSLinkComponent
            href={failingUrl}
            className={"artifact-url"}
            telemetrySource={telemetryEvent}>
            {this.props.failingContextName}
        </FPSLinkComponent>;
    }

    private _calculateCompletedSince(): string {
        let sb: string = "";

        if (defaultComparer(this.props.state, "Completed") === 0 && this.props.completedDate) {
            sb = sb + " ";

            // date will be in client time zone. ago will compare date in client time zone.
            sb = sb + ago(this.props.completedDate);
        }
        else if (this.props.startedDate) {
            sb = sb + " ";

            // date will be in client time zone. ago will compare date in client time zone.
            sb = sb + ago(this.props.startedDate);
        }

        return sb.toString();
    }

    private _getOutcomeAndComputerNameInfoText(): JSX.Element {
        const outcome: string = this.props.outcome ? getFriendlyName(this.props.outcome) : "";
        const completedSince: string = this._calculateCompletedSince();
        return (
            this.props.computerName
                ? <span className="test-results-outcome-machine-name-info">
                    {
                        format(
                            Resources.ResultOutcomeAndMachineNameInfo,
                            outcome,
                            completedSince,
                            this.props.computerName
                        )
                    }
                </span>
                : <span className="test-result-outcome-info">
                    {
                        format(
                            Resources.ResultOutcomeInfo,
                            outcome,
                            completedSince
                        )
                    }
                </span>
        );
    }

    private _getOutcomeIcon(): JSX.Element {
        let iconDetails: IconDetails;
        if (this.props.outcome) {
            iconDetails = getIconDetails(this.props.outcome);
        } else {
            iconDetails = getIconDetails(TestOutcome.None);
        }
        return (
            <Icon iconName={iconDetails.iconName} className={`${iconDetails.className} testresult-outcome-icon`} />
        );
    }

    private _getBuildUrl(buildId: number) {
        const newBuildHubRoute: string = "ms.vss-build-web.ci-results-hub-route";
        let tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["buildId"] = buildId.toString();
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";

        return routeUrl(this.context.pageContext, newBuildHubRoute, routeValues);
    }

    private _getReleaseUrl(releaseId: number) {
        const newReleaseHubRoute: string = "ms.vss-releaseManagement-web.cd-release-progress-default-route";
        let tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["releaseId"] = releaseId.toString();
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";

        return routeUrl(this.context.pageContext, newReleaseHubRoute, routeValues);
    }
}
